import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCoffee, FiDroplet, FiCheck, FiX, FiPlay } = FiIcons;

const onboardingSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Barista Master!',
    content: 'Learn to craft authentic coffee drinks with proper barista techniques. Each drink has a specific sequence that must be followed.',
    visual: '☕',
    action: 'Let\'s Start!'
  },
  {
    id: 'basics',
    title: 'Coffee Basics',
    content: 'Every coffee starts with espresso shots. Watch the equipment glow when it\'s time to use it. Follow the sequence exactly!',
    visual: '🔥',
    action: 'Got it!'
  },
  {
    id: 'sequence',
    title: 'Sequence Matters',
    content: 'Real baristas follow specific steps:\n1. Espresso first\n2. Steam milk\n3. Add foam\n4. Serve immediately',
    visual: '📋',
    action: 'Understood!'
  },
  {
    id: 'tutorial',
    title: 'Practice Time',
    content: 'Let\'s make your first latte! Follow the glowing ingredients in order. Don\'t worry - we\'ll guide you through it.',
    visual: '🎯',
    action: 'Start Tutorial'
  }
];

function OnboardingOverlay({ onComplete, onStartTutorial }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Start tutorial mode
      setIsVisible(false);
      onStartTutorial();
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    onComplete();
  };

  if (!isVisible) return null;

  const step = onboardingSteps[currentStep];

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl text-center max-w-sm w-full"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 500 }}
      >
        {/* Visual */}
        <div className="text-6xl mb-4">
          {step.visual}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {step.title}
        </h2>

        {/* Content */}
        <p className="text-gray-600 mb-6 leading-relaxed whitespace-pre-line">
          {step.content}
        </p>

        {/* Progress Dots */}
        <div className="flex justify-center space-x-2 mb-6">
          {onboardingSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep ? 'bg-amber-500' : 
                index < currentStep ? 'bg-green-400' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <motion.button
            className="flex-1 bg-gray-200 text-gray-600 py-3 rounded-2xl font-bold"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSkip}
          >
            Skip
          </motion.button>
          
          <motion.button
            className="flex-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-6 rounded-2xl font-bold"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleNext}
          >
            {step.action}
            <SafeIcon icon={FiArrowRight} className="inline ml-2" />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default OnboardingOverlay;